import React, { useEffect, useState } from "react";
import { announcementService } from "../../firebase/services";
import {
  Bell,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  AlertCircle,
  Calendar,
} from "lucide-react";

export const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    id: null,
    title: "",
    content: "",
    priority: "normal",
    author: "Kian Technologies Team",
  });

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const list = await announcementService.getAnnouncements();
      setAnnouncements(list);
    } catch (err) {
      console.error("Fetch announcements error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleOpenAdd = () => {
    setForm({
      id: null,
      title: "",
      content: "",
      priority: "normal",
      author: "Masterclass Coordinator",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setForm({
      id: item.id,
      title: item.title,
      content: item.content,
      priority: item.priority || "normal",
      author: item.author || "Kian Technologies",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      return alert("Please enter both title and content.");
    }

    setSaving(true);
    try {
      if (form.id) {
        await announcementService.updateAnnouncement(form.id, form);
      } else {
        await announcementService.createAnnouncement(form);
      }
      setShowModal(false);
      fetchAnnouncements();
    } catch (err) {
      console.error("Save announcement error:", err);
      alert(err.message || "Failed to save announcement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await announcementService.deleteAnnouncement(id);
      fetchAnnouncements();
    } catch (err) {
      console.error("Delete announcement error:", err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Masterclass Broadcasts & Announcements</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Publish notices, CTF lab credentials, and session updates to student dashboards.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          data-testid="admin-add-announcement-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-semibold shadow-md transition-all self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>New Announcement</span>
        </button>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <span className="text-xs font-mono">Loading announcements from Firestore...</span>
          </div>
        ) : announcements.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground space-y-2">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="text-sm font-semibold text-foreground">No announcements published yet.</div>
            <p className="text-xs max-w-sm mx-auto font-mono">
              Click "New Announcement" to send official updates to students.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {announcements.map((item) => (
              <div
                key={item.id}
                className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
              >
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`text-[10px] font-mono px-2 py-0.5 rounded uppercase font-bold border ${
                        item.priority === "urgent"
                          ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                          : item.priority === "important"
                          ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                          : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      }`}
                    >
                      {item.priority || "Normal"}
                    </span>
                    <h3 className="text-sm font-bold text-foreground font-sans">{item.title}</h3>
                  </div>

                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed whitespace-pre-line">
                    {item.content}
                  </p>

                  <div className="text-[11px] font-mono text-muted-foreground flex items-center gap-2 pt-1">
                    <Calendar className="w-3 h-3 text-cyan-400" />
                    <span>
                      {item.createdAt?.toDate
                        ? item.createdAt.toDate().toLocaleDateString()
                        : "Just now"}
                    </span>
                    {item.author && <span>• Author: {item.author}</span>}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenEdit(item)}
                    data-testid={`edit-ann-${item.id}`}
                    className="p-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground border border-border text-xs transition-colors"
                    title="Edit Announcement"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-ann-${item.id}`}
                    className="p-2 rounded-lg bg-rose-600/10 hover:bg-rose-600/20 text-rose-400 border border-rose-500/20 transition-colors"
                    title="Delete Announcement"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-card border border-border max-w-lg w-full rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-bold text-base">
                {form.id ? "Edit Announcement" : "Publish New Announcement"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground text-xs font-mono"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs font-mono">
              <div>
                <label className="block font-medium text-foreground mb-1">Headline / Title *</label>
                <input
                  type="text"
                  required
                  data-testid="admin-ann-title-input"
                  placeholder="e.g. Day 07 CTF Server Access & Credentials"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-foreground mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="normal">Normal Notice</option>
                    <option value="important">Important Update</option>
                    <option value="urgent">Urgent Alert</option>
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-foreground mb-1">Posted By</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium text-foreground mb-1">Announcement Content *</label>
                <textarea
                  rows={4}
                  required
                  data-testid="admin-ann-content-textarea"
                  placeholder="Type the message for enrolled students..."
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-input focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-ann-submit-btn"
                  className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  <span>Publish</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
